import { DistanceSuggester } from '../../domain/services/DistanceSuggester.js';
import { Toast } from '../common/Toast.js';
import { readTextFromClipboard } from '../common/clipboard.js';

/**
 * RaceForm — modal overlay form for adding or editing a race entry.
 *
 * Opens over the full viewport with a dark backdrop.
 * Validates fields on submit and communicates back via onSubmit / onCancel.
 */
export class RaceForm {
  /**
   * @param {Object} opts
   * @param {Function} opts.onSubmit - called with race data object
   * @param {Function} opts.onCancel - called when form is dismissed
   */
  constructor({ onSubmit, onCancel }) {
    this._onSubmit = onSubmit;
    this._onCancel = onCancel;
    this._overlay = null;
    this._existingRace = null;

    // Field references
    this._nameInput = null;
    this._distanceInput = null;
    this._timeInput = null;
    this._dateInput = null;
    this._locationInput = null;
    this._elevationInput = null;
    this._elevationGroup = null;
    this._highlightCheckbox = null;
    this._roadBtn = null;
    this._trailBtn = null;
    this._currentType = 'road';

    this._userOverrodeDistance = false;

    this._build();
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Show the form. If existingRace is provided, populate for editing.
   * @param {Object|null} existingRace
   */
  show(existingRace = null) {
    this._existingRace = existingRace;
    this._clearForm();

    if (existingRace) {
      this._populateForm(existingRace);
      this._titleEl.textContent = 'Edit Race';
      this._submitBtn.textContent = 'Save Changes';
    } else {
      this._titleEl.textContent = 'Add Race';
      this._submitBtn.textContent = 'Add Race';
    }

    document.body.appendChild(this._overlay);
    document.addEventListener('keydown', this._onKeyDown);
    // Trigger animation on next frame
    requestAnimationFrame(() => {
      this._overlay.classList.add('race-form-overlay--visible');
    });
  }

  hide() {
    document.removeEventListener('keydown', this._onKeyDown);
    this._overlay.classList.remove('race-form-overlay--visible');
    // Remove from DOM after animation
    setTimeout(() => {
      if (this._overlay.parentNode) {
        this._overlay.parentNode.removeChild(this._overlay);
      }
    }, 250);
  }

  /* ------------------------------------------------------------------ */
  /*  Internal: build the DOM once                                       */
  /* ------------------------------------------------------------------ */

  _build() {
    // Overlay
    const overlay = document.createElement('div');
    overlay.className = 'race-form-overlay';
    this._overlay = overlay;

    // Close on backdrop click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.hide();
        this._onCancel?.();
      }
    });

    // Container
    const container = document.createElement('div');
    container.className = 'race-form-container';
    overlay.appendChild(container);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'race-form-close-btn';
    closeBtn.type = 'button';
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', () => {
      this.hide();
      this._onCancel?.();
    });
    container.appendChild(closeBtn);

    // Title
    const title = document.createElement('h3');
    title.className = 'race-form-title';
    title.textContent = 'Add Race';
    this._titleEl = title;
    container.appendChild(title);

    // Fields wrapper
    const fields = document.createElement('div');
    fields.className = 'race-form-fields';
    container.appendChild(fields);

    // --- Race Name ---
    this._nameInput = this._addInputGroup(fields, 'RACE NAME', 'text', 'e.g. Boston Marathon');
    this._nameInput.addEventListener('input', () => this._onNameInput());

    // --- Distance + Time row ---
    const row1 = document.createElement('div');
    row1.className = 'race-form-row';
    fields.appendChild(row1);

    const distGroup = this._buildInputGroupEl('DISTANCE (KM)', 'number', 'e.g. 42.195');
    this._distanceInput = distGroup.querySelector('.editor-input');
    this._distanceInput.min = '0';
    this._distanceInput.step = '0.001';
    this._distanceInput.addEventListener('input', () => {
      this._userOverrodeDistance = true;
    });
    row1.appendChild(distGroup);

    const timeGroup = this._buildInputGroupEl('TIME', 'text', 'HH:MM:SS or DNF');
    this._timeInput = timeGroup.querySelector('.editor-input');
    this._timeGroup = timeGroup;
    row1.appendChild(timeGroup);

    // --- Timed / Untimed toggle ---
    const timedGroup = document.createElement('div');
    timedGroup.className = 'editor-input-group';

    const timedLabel = document.createElement('label');
    timedLabel.className = 'editor-input-label';
    timedLabel.textContent = 'RACE CATEGORY';
    timedGroup.appendChild(timedLabel);

    const timedToggle = document.createElement('div');
    timedToggle.className = 'race-type-toggle';

    const timedBtn = document.createElement('button');
    timedBtn.type = 'button';
    timedBtn.className = 'race-type-btn race-type-btn--active';
    timedBtn.textContent = 'Timed';
    this._timedBtn = timedBtn;

    const untimedBtn = document.createElement('button');
    untimedBtn.type = 'button';
    untimedBtn.className = 'race-type-btn';
    untimedBtn.textContent = 'Untimed';
    this._untimedBtn = untimedBtn;

    timedBtn.addEventListener('click', () => this._setTimed(true));
    untimedBtn.addEventListener('click', () => this._setTimed(false));

    timedToggle.append(timedBtn, untimedBtn);
    timedGroup.appendChild(timedToggle);

    const timedDisclaimer = document.createElement('div');
    timedDisclaimer.className = 'race-form-disclaimer';
    timedDisclaimer.textContent = 'Only timed races count towards your stats (distance, elevation, PRs)';
    timedGroup.appendChild(timedDisclaimer);
    this._timedDisclaimer = timedDisclaimer;

    fields.appendChild(timedGroup);
    this._isTimed = true;

    // --- Location (optional) ---
    this._locationInput = this._addInputGroup(fields, 'LOCATION (OPTIONAL)', 'text', 'e.g. Boston, MA');

    // --- Road / Trail toggle ---
    const typeGroup = document.createElement('div');
    typeGroup.className = 'editor-input-group';
    typeGroup.style.marginBottom = '0';

    const typeLabel = document.createElement('label');
    typeLabel.className = 'editor-input-label';
    typeLabel.textContent = 'TYPE';
    typeGroup.appendChild(typeLabel);

    const typeToggle = document.createElement('div');
    typeToggle.className = 'race-type-toggle';

    const roadBtn = document.createElement('button');
    roadBtn.type = 'button';
    roadBtn.className = 'race-type-btn race-type-btn--active';
    roadBtn.textContent = 'Road';
    this._roadBtn = roadBtn;

    const trailBtn = document.createElement('button');
    trailBtn.type = 'button';
    trailBtn.className = 'race-type-btn';
    trailBtn.textContent = 'Trail';
    this._trailBtn = trailBtn;

    roadBtn.addEventListener('click', () => this._setType('road'));
    trailBtn.addEventListener('click', () => this._setType('trail'));

    typeToggle.appendChild(roadBtn);
    typeToggle.appendChild(trailBtn);
    typeGroup.appendChild(typeToggle);
    fields.appendChild(typeGroup);

    // --- Elevation (only visible for trail) ---
    const elevGroup = this._buildInputGroupEl('ELEVATION GAIN (M)', 'number', 'e.g. 1500');
    elevGroup.classList.add('race-form-elevation');
    this._elevationGroup = elevGroup;
    this._elevationInput = elevGroup.querySelector('.editor-input');
    this._elevationInput.min = '0';
    fields.appendChild(elevGroup);

    // --- Date ---
    this._dateInput = this._addInputGroup(fields, 'DATE', 'text', 'YYYY or YYYY-MM-DD');

    // --- Results/Certificate link (optional) ---
    const resultsLinkGroup = this._buildInputGroupEl('TIMING CERTIFICATE (OPTIONAL)', 'text', 'https://...');
    resultsLinkGroup.classList.add('editor-input-group--with-action');
    this._resultsLinkInput = resultsLinkGroup.querySelector('.editor-input');
    this._resultsLinkInput.inputMode = 'url';
    this._resultsLinkInput.autocapitalize = 'off';
    this._resultsLinkInput.autocomplete = 'off';
    this._resultsLinkInput.spellcheck = false;
    this._resultsLinkInput.addEventListener('paste', (event) => this._handleResultsLinkPaste(event));

    const resultsPasteBtn = document.createElement('button');
    resultsPasteBtn.type = 'button';
    resultsPasteBtn.className = 'editor-input-action-btn';
    resultsPasteBtn.textContent = 'Paste';
    resultsPasteBtn.addEventListener('click', async () => this._pasteResultsLinkFromClipboard());
    resultsLinkGroup.appendChild(resultsPasteBtn);
    fields.appendChild(resultsLinkGroup);

    // --- Notes (optional, max 150 chars) ---
    const notesGroup = document.createElement('div');
    notesGroup.className = 'editor-input-group';
    const notesLabel = document.createElement('label');
    notesLabel.className = 'editor-input-label';
    notesLabel.textContent = 'NOTES (OPTIONAL)';
    notesGroup.appendChild(notesLabel);
    const notesInput = document.createElement('textarea');
    notesInput.className = 'editor-input';
    notesInput.placeholder = 'e.g. First ultra finish, ran with dad, monsoon conditions...';
    notesInput.maxLength = 150;
    notesInput.rows = 2;
    notesInput.style.resize = 'vertical';
    notesGroup.appendChild(notesInput);
    const charCount = document.createElement('div');
    charCount.className = 'editor-char-count';
    charCount.textContent = '0 / 150';
    notesGroup.appendChild(charCount);
    notesInput.addEventListener('input', () => {
      charCount.textContent = `${notesInput.value.length} / 150`;
    });
    this._notesInput = notesInput;
    fields.appendChild(notesGroup);

    // --- Highlight checkbox ---
    const highlightWrap = document.createElement('div');
    highlightWrap.className = 'race-form-highlight';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'race-form-highlight-checkbox';
    checkbox.id = 'race-highlight-toggle';
    this._highlightCheckbox = checkbox;

    const checkLabel = document.createElement('label');
    checkLabel.className = 'race-form-highlight-label';
    checkLabel.htmlFor = 'race-highlight-toggle';
    checkLabel.textContent = 'Include in highlights';

    highlightWrap.appendChild(checkbox);
    highlightWrap.appendChild(checkLabel);
    fields.appendChild(highlightWrap);

    // --- Podium checkbox ---
    const podiumWrap = document.createElement('div');
    podiumWrap.className = 'race-form-highlight';

    const podiumCheckbox = document.createElement('input');
    podiumCheckbox.type = 'checkbox';
    podiumCheckbox.className = 'race-form-highlight-checkbox';
    podiumCheckbox.id = 'race-podium-toggle';
    this._podiumCheckbox = podiumCheckbox;

    const podiumLabel = document.createElement('label');
    podiumLabel.className = 'race-form-highlight-label';
    podiumLabel.htmlFor = 'race-podium-toggle';
    podiumLabel.textContent = '🏆 Podium finish';

    podiumWrap.appendChild(podiumCheckbox);
    podiumWrap.appendChild(podiumLabel);
    fields.appendChild(podiumWrap);

    // --- Actions ---
    const actions = document.createElement('div');
    actions.className = 'race-form-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'race-form-btn race-form-btn--cancel';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => {
      this.hide();
      this._onCancel?.();
    });

    const submitBtn = document.createElement('button');
    submitBtn.type = 'button';
    submitBtn.className = 'race-form-btn race-form-btn--submit';
    submitBtn.textContent = 'Add Race';
    this._submitBtn = submitBtn;
    submitBtn.addEventListener('click', () => this._handleSubmit());

    actions.appendChild(cancelBtn);
    actions.appendChild(submitBtn);
    container.appendChild(actions);

    // Escape key handler — attached/detached with show()/hide()
    this._onKeyDown = (e) => {
      if (e.key === 'Escape' && this._overlay.parentNode) {
        this.hide();
        this._onCancel?.();
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /*  Internal: helpers                                                  */
  /* ------------------------------------------------------------------ */

  _addInputGroup(parent, labelText, type, placeholder) {
    const group = this._buildInputGroupEl(labelText, type, placeholder);
    parent.appendChild(group);
    return group.querySelector('.editor-input');
  }

  _buildInputGroupEl(labelText, type, placeholder) {
    const group = document.createElement('div');
    group.className = 'editor-input-group';

    const label = document.createElement('label');
    label.className = 'editor-input-label';
    label.textContent = labelText;
    group.appendChild(label);

    const input = document.createElement('input');
    input.type = type;
    input.className = 'editor-input';
    input.placeholder = placeholder;
    group.appendChild(input);

    return group;
  }

  _setType(type) {
    this._currentType = type;
    this._roadBtn.classList.toggle('race-type-btn--active', type === 'road');
    this._trailBtn.classList.toggle('race-type-btn--active', type === 'trail');

    if (type === 'trail') {
      this._elevationGroup.classList.add('race-form-elevation--visible');
    } else {
      this._elevationGroup.classList.remove('race-form-elevation--visible');
    }
  }

  _setTimed(isTimed) {
    this._isTimed = isTimed;
    this._timedBtn.classList.toggle('race-type-btn--active', isTimed);
    this._untimedBtn.classList.toggle('race-type-btn--active', !isTimed);
    this._timeGroup.style.display = isTimed ? '' : 'none';
    this._timedDisclaimer.style.display = isTimed ? 'none' : '';
    if (!isTimed) {
      this._timeInput.value = '';
    }
  }

  _onNameInput() {
    const name = this._nameInput.value;
    if (this._userOverrodeDistance) return;

    const suggested = DistanceSuggester.suggest(name);
    if (suggested != null) {
      this._distanceInput.value = suggested;
    }
  }

  _normalizeResultsLink(value) {
    if (!value) return '';

    const collapsed = value.trim().replace(/\s+/g, ' ');
    const urlMatch = collapsed.match(/https?:\/\/[^\s"'<>]+/i);
    const candidate = (urlMatch ? urlMatch[0] : collapsed).replace(/[),.;]+$/, '');

    try {
      const parsed = new URL(candidate);
      if (!['http:', 'https:'].includes(parsed.protocol)) return '';
      return parsed.toString();
    } catch {
      return '';
    }
  }

  _handleResultsLinkPaste(event) {
    const pastedText = event.clipboardData?.getData('text');
    if (!pastedText) return;

    const normalizedLink = this._normalizeResultsLink(pastedText);
    if (!normalizedLink) return;

    event.preventDefault();
    this._resultsLinkInput.value = normalizedLink;
  }

  async _pasteResultsLinkFromClipboard() {
    try {
      const clipboardText = await readTextFromClipboard();
      const normalizedLink = this._normalizeResultsLink(clipboardText);

      if (!normalizedLink) {
        Toast.show('Clipboard does not contain a valid timing certificate link.', 'error');
        return;
      }

      this._resultsLinkInput.value = normalizedLink;
      this._resultsLinkInput.focus();
      this._resultsLinkInput.select();
      Toast.show('Timing certificate link pasted.', 'success');
    } catch (error) {
      console.error('Failed to read timing certificate link from clipboard:', error);
      Toast.show('Clipboard access is blocked. Paste the link manually.', 'error');
    }
  }

  _clearForm() {
    this._nameInput.value = '';
    this._distanceInput.value = '';
    this._timeInput.value = '';
    this._dateInput.value = '';
    this._locationInput.value = '';
    this._resultsLinkInput.value = '';
    this._notesInput.value = '';
    this._elevationInput.value = '';
    this._highlightCheckbox.checked = true;
    this._podiumCheckbox.checked = false;
    this._setType('road');
    this._setTimed(true);
    this._userOverrodeDistance = false;
    this._clearErrors();
  }

  _populateForm(race) {
    this._nameInput.value = race.raceName || '';
    this._distanceInput.value = race.distance != null ? race.distance : '';
    this._timeInput.value = race.time || '';
    this._dateInput.value = race.date || '';
    this._locationInput.value = race.location || '';
    this._resultsLinkInput.value = race.resultsLink || '';
    this._notesInput.value = race.notes || '';
    this._highlightCheckbox.checked = race.isHighlighted || false;
    this._podiumCheckbox.checked = race.isPodium || false;
    this._setType(race.type || 'road');
    this._setTimed(race.isTimed !== false); // default to timed
    if (race.type === 'trail' && race.elevation != null) {
      this._elevationInput.value = race.elevation;
    }
    // Don't auto-suggest distance when editing
    this._userOverrodeDistance = true;
  }

  _clearErrors() {
    const errors = this._overlay.querySelectorAll('.editor-field-error');
    errors.forEach((el) => el.remove());
    const errorInputs = this._overlay.querySelectorAll('.editor-input--error');
    errorInputs.forEach((el) => el.classList.remove('editor-input--error'));
  }

  _showFieldError(input, message) {
    input.classList.add('editor-input--error');
    const err = document.createElement('div');
    err.className = 'editor-field-error';
    err.textContent = message;
    input.parentNode.appendChild(err);
  }

  _handleSubmit() {
    this._clearErrors();

    const raceName = this._nameInput.value.trim();
    const distanceVal = this._distanceInput.value.trim();
    const timeVal = this._timeInput.value.trim();
    const dateVal = this._dateInput.value.trim();
    const locationVal = this._locationInput.value.trim();
    const resultsLinkVal = this._resultsLinkInput.value.trim();
    const notesVal = this._notesInput.value.trim();
    const elevationVal = this._elevationInput.value.trim();
    const isHighlighted = this._highlightCheckbox.checked;
    const isPodium = this._podiumCheckbox.checked;
    const type = this._currentType;

    let hasError = false;

    // Validate raceName
    if (!raceName) {
      this._showFieldError(this._nameInput, 'Race name is required');
      hasError = true;
    }

    // Validate distance
    const distance = parseFloat(distanceVal);
    if (!distanceVal || isNaN(distance) || distance <= 0) {
      this._showFieldError(this._distanceInput, 'Distance must be greater than 0');
      hasError = true;
    }

    // Validate time format if provided
    let time = null;
    if (timeVal) {
      const isDNF = timeVal.toUpperCase() === 'DNF';
      if (isDNF) {
        time = 'DNF';
      } else if (!/^\d{1,2}:\d{2}:\d{2}$/.test(timeVal)) {
        this._showFieldError(this._timeInput, 'Use HH:MM:SS format or DNF');
        hasError = true;
      } else {
        time = timeVal;
      }
    }

    // Validate elevation for trail
    let elevation = null;
    if (type === 'trail') {
      const elev = parseFloat(elevationVal);
      if (!elevationVal || isNaN(elev) || elev <= 0) {
        this._showFieldError(this._elevationInput, 'Elevation is required for trail races');
        hasError = true;
      } else {
        elevation = elev;
      }
    }

    const resultsLink = resultsLinkVal ? this._normalizeResultsLink(resultsLinkVal) : null;
    if (resultsLinkVal && !resultsLink) {
      this._showFieldError(this._resultsLinkInput, 'Paste a full http(s) timing certificate link');
      hasError = true;
    }

    if (hasError) return;

    const raceData = {
      raceName,
      distance,
      time,
      type,
      elevation,
      date: dateVal || null,
      location: locationVal || null,
      resultsLink,
      notes: notesVal || null,
      isTimed: this._isTimed,
      isHighlighted,
      isPodium,
    };

    // Preserve id and isPR if editing
    if (this._existingRace) {
      raceData.id = this._existingRace.id;
      raceData.isPR = this._existingRace.isPR || false;
    }

    this._onSubmit?.(raceData);
    this.hide();
  }
}
